export const SALES_REPORT_TIMEZONE = 'America/Guayaquil';
export const SALES_REPORT_MAX_RANGE_DAYS = 366;
export const SALES_REPORT_PAGE_SIZES = [20, 50, 100];

export const BASIS_OPTIONS = [
    {
        value: 'paid',
        label: 'Pagadas',
        hint: 'Por fecha de pago. Es el reporte por defecto.',
    },
    {
        value: 'created',
        label: 'Creadas',
        hint: 'Por fecha de creación. Las canceladas y las cotizaciones quedan fuera salvo que las incluyas.',
    },
    {
        value: 'delivered',
        label: 'Entregadas',
        hint: 'Por fecha de entrega.',
    },
    {
        value: 'invoiced',
        label: 'Facturadas',
        hint: 'Solo pedidos facturados. La fecha es la de creación porque no se guarda la hora de autorización.',
    },
];

export const GROUP_OPTIONS = [
    { value: 'day', label: 'Día' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
];

export const CUSTOMER_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'registered', label: 'Registrados' },
    { value: 'guest', label: 'Invitados' },
];

export const ORDER_STATUS_OPTIONS = [
    'CREATED',
    'PAYMENT_PENDING',
    'PAYMENT_CONFIRMED',
    'INVOICE_PENDING',
    'INVOICED',
    'INVOICE_FAILED',
    'CANCELLED',
    'REFUNDED',
    'QUOTATION_PENDING',
];

export const ORDER_STATUS_LABELS = {
    CREATED: 'Creado',
    PAYMENT_PENDING: 'Pago pendiente',
    PAYMENT_CONFIRMED: 'Pago confirmado',
    INVOICE_PENDING: 'Factura pendiente',
    INVOICED: 'Facturado',
    INVOICE_FAILED: 'Factura fallida',
    CANCELLED: 'Cancelado',
    REFUNDED: 'Reembolsado',
    QUOTATION_PENDING: 'Cotización',
};

export const PAYMENT_GROUP_LABELS = {
    PAYPHONE: 'PayPhone',
    BANK_TRANSFER: 'Transferencia',
};

export const FALLBACK_PAYMENT_GROUPS = [
    { key: 'PAYPHONE', values: ['PayPhone', 'PAYPHONE', 'payphone'] },
    { key: 'BANK_TRANSFER', values: ['BANK_TRANSFER', 'Transfer', 'TRANSFER', 'Bank Transfer'] },
];

export const ORDER_SORT_OPTIONS = [
    { value: 'paidAt', label: 'Fecha de pago' },
    { value: 'createdAt', label: 'Fecha de creación' },
    { value: 'deliveredAt', label: 'Fecha de entrega' },
    { value: 'totalPrice', label: 'Total' },
    { value: 'itemsPrice', label: 'Subtotal' },
    { value: 'orderStatus', label: 'Estado' },
    { value: 'paymentMethod', label: 'Método de pago' },
];

export const PRODUCT_SORT_OPTIONS = [
    { value: 'lineNet', label: 'Venta neta' },
    { value: 'lineGross', label: 'Venta bruta' },
    { value: 'lineDiscount', label: 'Descuento' },
    { value: 'unitsSold', label: 'Unidades' },
    { value: 'orderCount', label: 'Pedidos' },
    { value: 'name', label: 'Nombre' },
];

export const REPORT_TABS = [
    { id: 'resumen', label: 'Resumen' },
    { id: 'pedidos', label: 'Pedidos' },
    { id: 'productos', label: 'Productos' },
];

const BASIS_VALUES = new Set(BASIS_OPTIONS.map((option) => option.value));
const GROUP_VALUES = new Set(GROUP_OPTIONS.map((option) => option.value));
const ORDER_SORT_VALUES = new Set(ORDER_SORT_OPTIONS.map((option) => option.value));
const PRODUCT_SORT_VALUES = new Set(PRODUCT_SORT_OPTIONS.map((option) => option.value));
const TAB_VALUES = new Set(REPORT_TABS.map((tab) => tab.id));

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const BASIS_DATE_FIELD = {
    paid: 'paidAt',
    created: 'createdAt',
    delivered: 'deliveredAt',
    invoiced: 'createdAt',
};

const SERVER_MESSAGE_LABELS = {
    'Date range cannot exceed 366 days.': 'El rango no puede superar 366 días.',
    'startDate and endDate must be sent together.': 'Indica la fecha de inicio y la fecha de fin.',
    'endDate must be on or after startDate.': 'La fecha fin debe ser igual o posterior a la fecha inicio.',
    'minTotal cannot be greater than maxTotal.': 'El total mínimo no puede ser mayor que el máximo.',
    'keyword cannot exceed 80 characters.': 'La búsqueda no puede superar 80 caracteres.',
    'keyword must be at least 2 characters.': 'La búsqueda necesita al menos 2 caracteres.',
    'isPaid=false conflicts with basis paid.': 'El criterio de pagadas no admite pedidos sin pago.',
    'isDelivered=false conflicts with basis delivered.': 'El criterio de entregadas no admite pedidos sin entrega.',
    'orderStatus must be INVOICED when basis is invoiced.': 'Con el criterio facturadas el estado debe ser facturado.',
    'Not authorized to view sales reports': 'No tienes permiso para ver reportes de ventas.',
    'Not authorized to view sales for this company': 'No tienes permiso para ver las ventas de esta empresa.',
    'Admin user has no company access configured': 'Tu usuario no tiene empresas asignadas.',
    'Not authorized, no token': 'Tu sesión expiró. Vuelve a iniciar sesión.',
};

const listFromParam = (value) =>
    String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

const datePart = (date, type) =>
    new Intl.DateTimeFormat('en-US', {
        timeZone: SALES_REPORT_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })
        .formatToParts(date)
        .find((part) => part.type === type)?.value;

export const ecuadorTodayLabel = (date = new Date()) =>
    `${datePart(date, 'year')}-${datePart(date, 'month')}-${datePart(date, 'day')}`;

export const addIsoDays = (isoDate, days) => {
    const [year, month, day] = String(isoDate).split('-').map(Number);
    const next = new Date(Date.UTC(year, month - 1, day));
    next.setUTCDate(next.getUTCDate() + days);
    return next.toISOString().slice(0, 10);
};

export const inclusiveDayCount = (startDate, endDate) => {
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    const start = Date.UTC(startYear, startMonth - 1, startDay);
    const end = Date.UTC(endYear, endMonth - 1, endDay);
    return Math.round((end - start) / 86400000) + 1;
};

export const currentMonthRange = (today = ecuadorTodayLabel()) => ({
    startDate: `${today.slice(0, 8)}01`,
    endDate: today,
});

export const previousMonthRange = (today = ecuadorTodayLabel()) => {
    const [year, month] = today.split('-').map(Number);
    const previous = month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
    const monthText = String(previous.month).padStart(2, '0');
    const lastDay = new Date(Date.UTC(previous.year, previous.month, 0)).getUTCDate();
    return {
        startDate: `${previous.year}-${monthText}-01`,
        endDate: `${previous.year}-${monthText}-${String(lastDay).padStart(2, '0')}`,
    };
};

export const lastDaysRange = (today, days) => ({
    startDate: addIsoDays(today, -(days - 1)),
    endDate: today,
});

export const yearToDateRange = (today = ecuadorTodayLabel()) => ({
    startDate: `${today.slice(0, 4)}-01-01`,
    endDate: today,
});

export const RANGE_PRESETS = [
    { id: 'month', label: 'Este mes', range: currentMonthRange },
    { id: 'previous-month', label: 'Mes anterior', range: previousMonthRange },
    { id: '7', label: 'Últimos 7 días', range: (today) => lastDaysRange(today, 7) },
    { id: '30', label: 'Últimos 30 días', range: (today) => lastDaysRange(today, 30) },
    { id: 'year', label: 'Este año', range: yearToDateRange },
];

export const matchingPreset = (startDate, endDate, today = ecuadorTodayLabel()) =>
    RANGE_PRESETS.find((preset) => {
        const range = preset.range(today);
        return range.startDate === startDate && range.endDate === endDate;
    })?.id || '';

export const createDefaultFilters = (today = ecuadorTodayLabel()) => ({
    ...currentMonthRange(today),
    basis: 'paid',
    groupBy: 'day',
    compare: true,
    ruc: [],
    orderStatus: [],
    paymentMethod: [],
    isPaid: '',
    isDelivered: '',
    finished: '',
    customerType: '',
    city: '',
    state: '',
    discountCode: '',
    minTotal: '',
    maxTotal: '',
    productId: '',
    contificoCode: '',
    keyword: '',
    includeCancelled: false,
    includeQuotations: false,
    page: 1,
    productPage: 1,
    pageSize: 20,
    sortBy: '',
    sortOrder: 'desc',
    productSortBy: '',
    tab: 'resumen',
});

const positiveInt = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export const readSalesReportSearch = (searchParams) => {
    const read = (key) => searchParams.get(key) || '';
    const basis = BASIS_VALUES.has(read('basis')) ? read('basis') : 'paid';
    const groupBy = GROUP_VALUES.has(read('groupBy')) ? read('groupBy') : 'day';
    const pageSize = positiveInt(read('pageSize'), 20);
    const customerType = read('customerType');

    return {
        startDate: read('startDate'),
        endDate: read('endDate'),
        basis,
        groupBy,
        compare: read('compare') !== 'false',
        ruc: listFromParam(read('ruc')),
        orderStatus: listFromParam(read('orderStatus')).map((status) => status.toUpperCase()),
        paymentMethod: listFromParam(read('paymentMethod')),
        isPaid: read('isPaid') === 'true' || read('isPaid') === 'false' ? read('isPaid') : '',
        isDelivered: read('isDelivered') === 'true' || read('isDelivered') === 'false' ? read('isDelivered') : '',
        finished: read('finished') === 'true' || read('finished') === 'false' ? read('finished') : '',
        customerType: customerType === 'registered' || customerType === 'guest' ? customerType : '',
        city: read('city'),
        state: read('state'),
        discountCode: read('discountCode'),
        minTotal: read('minTotal'),
        maxTotal: read('maxTotal'),
        productId: read('productId'),
        contificoCode: read('contificoCode'),
        keyword: read('keyword').slice(0, 80),
        includeCancelled: read('includeCancelled') === 'true',
        includeQuotations: read('includeQuotations') === 'true',
        page: positiveInt(read('page'), 1),
        productPage: positiveInt(read('productPage'), 1),
        pageSize: SALES_REPORT_PAGE_SIZES.includes(pageSize) ? pageSize : 20,
        sortBy: ORDER_SORT_VALUES.has(read('sortBy')) ? read('sortBy') : '',
        sortOrder: read('sortOrder') === 'asc' ? 'asc' : 'desc',
        productSortBy: PRODUCT_SORT_VALUES.has(read('productSortBy')) ? read('productSortBy') : '',
        tab: TAB_VALUES.has(read('tab')) ? read('tab') : 'resumen',
    };
};

const commaList = (values) => (Array.isArray(values) && values.length ? values.join(',') : '');

export const toSearchParams = (filters) => {
    const params = new URLSearchParams();
    const set = (key, value) => {
        if (value === undefined || value === null || value === '' || value === false) {
            return;
        }
        params.set(key, String(value));
    };

    set('startDate', filters.startDate);
    set('endDate', filters.endDate);
    if (filters.basis && filters.basis !== 'paid') set('basis', filters.basis);
    if (filters.groupBy && filters.groupBy !== 'day') set('groupBy', filters.groupBy);
    if (filters.compare === false) params.set('compare', 'false');
    set('ruc', commaList(filters.ruc));
    set('orderStatus', commaList(filters.orderStatus));
    set('paymentMethod', commaList(filters.paymentMethod));
    set('isPaid', filters.isPaid);
    set('isDelivered', filters.isDelivered);
    set('finished', filters.finished);
    set('customerType', filters.customerType);
    set('city', (filters.city || '').trim());
    set('state', (filters.state || '').trim());
    set('discountCode', (filters.discountCode || '').trim());
    set('minTotal', filters.minTotal);
    set('maxTotal', filters.maxTotal);
    set('productId', (filters.productId || '').trim());
    set('contificoCode', (filters.contificoCode || '').trim());
    set('keyword', (filters.keyword || '').trim());
    if (filters.includeCancelled) params.set('includeCancelled', 'true');
    if (filters.includeQuotations) params.set('includeQuotations', 'true');
    if (filters.page > 1) params.set('page', String(filters.page));
    if (filters.productPage > 1) params.set('productPage', String(filters.productPage));
    if (filters.pageSize && filters.pageSize !== 20) params.set('pageSize', String(filters.pageSize));
    set('sortBy', filters.sortBy);
    set('productSortBy', filters.productSortBy);
    if ((filters.sortBy || filters.productSortBy) && filters.sortOrder === 'asc') {
        params.set('sortOrder', 'asc');
    }
    if (filters.tab && filters.tab !== 'resumen') set('tab', filters.tab);
    return params;
};

export const salesReportFormKey = (filters) =>
    JSON.stringify({
        startDate: filters.startDate || '',
        endDate: filters.endDate || '',
        basis: filters.basis || 'paid',
        groupBy: filters.groupBy || 'day',
        compare: filters.compare !== false,
        ruc: filters.ruc || [],
        orderStatus: filters.orderStatus || [],
        paymentMethod: filters.paymentMethod || [],
        isPaid: filters.isPaid || '',
        isDelivered: filters.isDelivered || '',
        finished: filters.finished || '',
        customerType: filters.customerType || '',
        city: filters.city || '',
        state: filters.state || '',
        discountCode: filters.discountCode || '',
        minTotal: filters.minTotal ?? '',
        maxTotal: filters.maxTotal ?? '',
        productId: filters.productId || '',
        contificoCode: filters.contificoCode || '',
        keyword: filters.keyword || '',
        includeCancelled: Boolean(filters.includeCancelled),
        includeQuotations: Boolean(filters.includeQuotations),
    });

const splitCodes = (value) =>
    String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

export const sanitizeDraft = (draft) => {
    const next = {
        ...draft,
        basis: BASIS_VALUES.has(draft.basis) ? draft.basis : 'paid',
        groupBy: GROUP_VALUES.has(draft.groupBy) ? draft.groupBy : 'day',
        compare: draft.compare !== false,
        ruc: Array.isArray(draft.ruc) ? draft.ruc.filter(Boolean) : [],
        orderStatus: (Array.isArray(draft.orderStatus) ? draft.orderStatus : [])
            .map((status) => String(status).trim().toUpperCase())
            .filter((status) => ORDER_STATUS_OPTIONS.includes(status)),
        paymentMethod: Array.isArray(draft.paymentMethod) ? draft.paymentMethod.filter(Boolean) : [],
        city: (draft.city || '').trim(),
        state: (draft.state || '').trim(),
        discountCode: (draft.discountCode || '').trim(),
        keyword: (draft.keyword || '').trim(),
        productId: splitCodes(draft.productId).join(','),
        contificoCode: splitCodes(draft.contificoCode).join(','),
        minTotal: draft.minTotal ?? '',
        maxTotal: draft.maxTotal ?? '',
        includeCancelled: Boolean(draft.includeCancelled),
        includeQuotations: Boolean(draft.includeQuotations),
    };

    if (next.basis === 'paid' && next.isPaid === 'false') next.isPaid = '';
    if (next.basis === 'delivered' && next.isDelivered === 'false') next.isDelivered = '';
    if (next.basis === 'invoiced') next.orderStatus = [];
    return next;
};

export const validateSalesReportDraft = (draft) => {
    const errors = [];
    const startDate = (draft.startDate || '').trim();
    const endDate = (draft.endDate || '').trim();
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;

    if ((startDate && !endDate) || (!startDate && endDate)) {
        errors.push('Indica la fecha de inicio y la fecha de fin.');
    }

    if (startDate && endDate) {
        if (!datePattern.test(startDate) || !datePattern.test(endDate)) {
            errors.push('Usa fechas en formato año-mes-día.');
        } else if (endDate < startDate) {
            errors.push('La fecha fin debe ser igual o posterior a la fecha inicio.');
        } else if (inclusiveDayCount(startDate, endDate) > SALES_REPORT_MAX_RANGE_DAYS) {
            errors.push('El rango no puede superar 366 días.');
        }
    }

    const keyword = (draft.keyword || '').trim();
    if (keyword.length > 80) {
        errors.push('La búsqueda no puede superar 80 caracteres.');
    } else if (keyword && keyword.length < 2 && !/^[0-9a-fA-F]{24}$/.test(keyword)) {
        errors.push('La búsqueda necesita al menos 2 caracteres.');
    }

    const productIds = splitCodes(draft.productId);
    if (productIds.some((id) => !/^[0-9a-fA-F]{24}$/.test(id))) {
        errors.push('Cada ID de producto debe tener 24 caracteres hexadecimales.');
    }

    const minTotal = draft.minTotal === '' || draft.minTotal === undefined ? undefined : Number(draft.minTotal);
    const maxTotal = draft.maxTotal === '' || draft.maxTotal === undefined ? undefined : Number(draft.maxTotal);
    if (minTotal !== undefined && !Number.isFinite(minTotal)) {
        errors.push('El total mínimo no es un número válido.');
    }
    if (maxTotal !== undefined && !Number.isFinite(maxTotal)) {
        errors.push('El total máximo no es un número válido.');
    }
    if (minTotal !== undefined && maxTotal !== undefined && minTotal > maxTotal) {
        errors.push('El total mínimo no puede ser mayor que el máximo.');
    }

    if (draft.basis === 'paid' && draft.isPaid === 'false') {
        errors.push('El criterio de pagadas no admite pedidos sin pago.');
    }
    if (draft.basis === 'delivered' && draft.isDelivered === 'false') {
        errors.push('El criterio de entregadas no admite pedidos sin entrega.');
    }
    if (
        draft.basis === 'invoiced' &&
        Array.isArray(draft.orderStatus) &&
        draft.orderStatus.some((status) => status && status !== 'INVOICED')
    ) {
        errors.push('Con el criterio facturadas el estado debe ser facturado.');
    }

    return errors;
};

export const toReportQuery = (filters, kind = 'summary') => {
    const params = {
        basis: filters.basis || 'paid',
        groupBy: filters.groupBy || 'day',
    };

    if (filters.startDate && filters.endDate) {
        params.startDate = filters.startDate;
        params.endDate = filters.endDate;
    }
    if (filters.compare === false) params.compare = 'false';
    if (filters.ruc?.length) params.ruc = filters.ruc.join(',');
    if (filters.basis !== 'invoiced' && filters.orderStatus?.length) {
        params.orderStatus = filters.orderStatus.join(',');
    }
    if (filters.paymentMethod?.length) params.paymentMethod = filters.paymentMethod.join(',');
    if (filters.basis !== 'paid' && filters.isPaid) params.isPaid = filters.isPaid;
    if (filters.basis !== 'delivered' && filters.isDelivered) params.isDelivered = filters.isDelivered;
    if (filters.finished) params.finished = filters.finished;
    if (filters.customerType) params.customerType = filters.customerType;
    if ((filters.city || '').trim()) params.city = filters.city.trim();
    if ((filters.state || '').trim()) params.state = filters.state.trim();
    if ((filters.discountCode || '').trim()) params.discountCode = filters.discountCode.trim();
    if (filters.minTotal !== '' && filters.minTotal !== undefined && filters.minTotal !== null) {
        params.minTotal = filters.minTotal;
    }
    if (filters.maxTotal !== '' && filters.maxTotal !== undefined && filters.maxTotal !== null) {
        params.maxTotal = filters.maxTotal;
    }
    if ((filters.productId || '').trim()) params.productId = filters.productId.trim();
    if ((filters.contificoCode || '').trim()) params.contificoCode = filters.contificoCode.trim();
    if ((filters.keyword || '').trim()) params.keyword = filters.keyword.trim();
    if (filters.includeCancelled) params.includeCancelled = 'true';
    if (filters.includeQuotations) params.includeQuotations = 'true';

    if (kind === 'orders' || kind === 'products') {
        params.pageSize = filters.pageSize || 20;
        params.sortOrder = filters.sortOrder === 'asc' ? 'asc' : 'desc';
    }
    if (kind === 'orders') {
        params.pageNumber = filters.page || 1;
        if (filters.sortBy) params.sortBy = filters.sortBy;
    }
    if (kind === 'products') {
        params.pageNumber = filters.productPage || 1;
        if (filters.productSortBy) params.productSortBy = filters.productSortBy;
    }

    return params;
};

export const basisLabel = (basis) => BASIS_OPTIONS.find((option) => option.value === basis)?.label || 'Pagadas';

export const basisHint = (basis) => BASIS_OPTIONS.find((option) => option.value === basis)?.hint || '';

export const basisDateField = (basis) => BASIS_DATE_FIELD[basis] || 'paidAt';

export const activeOrderSort = (filters) => filters.sortBy || basisDateField(filters.basis);

export const activeProductSort = (filters) => filters.productSortBy || 'lineNet';

export const orderStatusLabel = (status) => ORDER_STATUS_LABELS[status] || status || 'Sin estado';

export const paymentMethodLabel = (value) => {
    if (!value || value === 'Unknown') return 'Sin método';
    const group = FALLBACK_PAYMENT_GROUPS.find(
        (item) =>
            item.key.toLowerCase() === String(value).toLowerCase() ||
            item.values.some((entry) => entry.toLowerCase() === String(value).toLowerCase())
    );
    return group ? PAYMENT_GROUP_LABELS[group.key] : String(value);
};

export const companyLabel = (company) => company?.tradeName || company?.legalName || company?.ruc || 'Empresa';

export const breakdownLabel = (row, kind) => {
    if (!row?.key || row.key === 'Unknown') return 'Sin dato';
    if (row.key === 'other') return 'Otros';
    if (kind === 'company') return companyLabel(row);
    if (kind === 'paymentMethod') return paymentMethodLabel(row.key);
    if (kind === 'orderStatus') return orderStatusLabel(row.key);
    return row.key;
};

export const formatMoney = (value) => {
    const amount = Number(value);
    const safe = Number.isFinite(amount) ? amount : 0;
    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Math.abs(safe));
    return safe < 0 ? `-$${formatted}` : `$${formatted}`;
};

export const formatCount = (value) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(Number(value) || 0);

export const formatCompact = (value, money = false) => {
    const amount = Number(value) || 0;
    const abs = Math.abs(amount);
    const sign = amount < 0 ? '-' : '';
    if (abs >= 1000000) {
        return `${sign}${money ? '$' : ''}${(abs / 1000000).toFixed(1)}M`;
    }
    if (abs >= 1000) {
        return `${sign}${money ? '$' : ''}${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
    }
    return money ? formatMoney(amount) : formatCount(amount);
};

export const formatPercent = (percent) => {
    if (percent === null || percent === undefined || Number.isNaN(Number(percent))) return 'Nuevo';
    const value = Number(percent);
    const digits = Math.abs(value) >= 100 ? 0 : 1;
    return `${value > 0 ? '+' : ''}${value.toFixed(digits)}%`;
};

export const formatDayLabel = (isoDate) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || '');
    if (!match) return isoDate || '';
    return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
};

export const formatShortDay = (isoDate) => formatDayLabel(isoDate).replace(/ \d{4}$/, '');

export const formatMonthLabel = (yearMonth) => {
    const match = /^(\d{4})-(\d{2})$/.exec(yearMonth || '');
    if (!match) return yearMonth || '';
    return `${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
};

export const formatPeriodLabel = (period, groupBy, { short = false } = {}) => {
    if (groupBy === 'month') return formatMonthLabel(period);
    if (groupBy === 'week') {
        const day = short ? formatShortDay(period) : formatDayLabel(period);
        return short ? day : `Semana del ${day}`;
    }
    return short ? formatShortDay(period) : formatDayLabel(period);
};

export const formatGuayaquilStamp = (value, withTime = false) => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('es-EC', {
        timeZone: SALES_REPORT_TIMEZONE,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    }).format(date);
};

export const formatExclusiveRange = (startIso, endExclusiveIso) => {
    if (!startIso || !endExclusiveIso) return '';
    const end = new Date(new Date(endExclusiveIso).getTime() - 1);
    const startLabel = ecuadorTodayLabel(new Date(startIso));
    const endLabel = ecuadorTodayLabel(end);
    if (!startLabel || Number.isNaN(end.getTime())) return '';
    return `${formatDayLabel(startLabel)} – ${formatDayLabel(endLabel)}`;
};

export const orderRecognitionDate = (order, basis) => {
    if (basis === 'delivered') return order?.deliveredAt;
    if (basis === 'created' || basis === 'invoiced') return order?.createdAt;
    return order?.paidAt || order?.createdAt;
};

export const shortId = (id) => {
    const value = String(id || '');
    return value.length > 8 ? value.slice(-8) : value;
};

export const getQueryErrorMessage = (error) => {
    if (!error) return '';
    const raw =
        (typeof error.data === 'string' && error.data) ||
        error.data?.message ||
        error.error ||
        '';
    if (SERVER_MESSAGE_LABELS[raw]) return SERVER_MESSAGE_LABELS[raw];
    if (raw) return raw;
    if (error.status === 403) return 'No tienes permiso para ver este reporte.';
    if (error.status === 401) return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    return 'No se pudo cargar el reporte de ventas.';
};

const chip = (id, label, clear) => ({ id, label, clear });

export const buildFilterChips = (filters, companies = []) => {
    const chips = [];
    if (filters.basis && filters.basis !== 'paid') {
        chips.push(chip('basis', `Criterio: ${basisLabel(filters.basis)}`, (current) => ({ ...current, basis: 'paid' })));
    }
    if (filters.groupBy && filters.groupBy !== 'day') {
        const label = GROUP_OPTIONS.find((option) => option.value === filters.groupBy)?.label || filters.groupBy;
        chips.push(chip('groupBy', `Agrupar: ${label}`, (current) => ({ ...current, groupBy: 'day' })));
    }
    if (filters.compare === false) {
        chips.push(chip('compare', 'Sin comparación', (current) => ({ ...current, compare: true })));
    }
    (filters.ruc || []).forEach((ruc) => {
        const company = companies.find((item) => item.ruc === ruc);
        chips.push(
            chip(`ruc-${ruc}`, companyLabel(company || { ruc }), (current) => ({
                ...current,
                ruc: current.ruc.filter((item) => item !== ruc),
            }))
        );
    });
    (filters.orderStatus || []).forEach((status) => {
        chips.push(
            chip(`status-${status}`, orderStatusLabel(status), (current) => ({
                ...current,
                orderStatus: current.orderStatus.filter((item) => item !== status),
            }))
        );
    });
    (filters.paymentMethod || []).forEach((method) => {
        chips.push(
            chip(`pay-${method}`, paymentMethodLabel(method), (current) => ({
                ...current,
                paymentMethod: current.paymentMethod.filter((item) => item !== method),
            }))
        );
    });
    if (filters.isPaid) {
        chips.push(
            chip('isPaid', filters.isPaid === 'true' ? 'Pagado' : 'No pagado', (current) => ({ ...current, isPaid: '' }))
        );
    }
    if (filters.isDelivered) {
        chips.push(
            chip(
                'isDelivered',
                filters.isDelivered === 'true' ? 'Entregado' : 'No entregado',
                (current) => ({ ...current, isDelivered: '' })
            )
        );
    }
    if (filters.finished) {
        chips.push(
            chip(
                'finished',
                filters.finished === 'true' ? 'Finalizado' : 'No finalizado',
                (current) => ({ ...current, finished: '' })
            )
        );
    }
    if (filters.customerType) {
        const label = CUSTOMER_OPTIONS.find((option) => option.value === filters.customerType)?.label;
        chips.push(chip('customerType', label || filters.customerType, (current) => ({ ...current, customerType: '' })));
    }
    if ((filters.city || '').trim()) {
        chips.push(chip('city', `Ciudad: ${filters.city.trim()}`, (current) => ({ ...current, city: '' })));
    }
    if ((filters.state || '').trim()) {
        chips.push(chip('state', `Provincia: ${filters.state.trim()}`, (current) => ({ ...current, state: '' })));
    }
    if ((filters.discountCode || '').trim()) {
        chips.push(
            chip('discountCode', `Cupón: ${filters.discountCode.trim()}`, (current) => ({ ...current, discountCode: '' }))
        );
    }
    if (filters.minTotal !== '' && filters.minTotal !== undefined && filters.minTotal !== null) {
        chips.push(chip('minTotal', `Mínimo ${formatMoney(filters.minTotal)}`, (current) => ({ ...current, minTotal: '' })));
    }
    if (filters.maxTotal !== '' && filters.maxTotal !== undefined && filters.maxTotal !== null) {
        chips.push(chip('maxTotal', `Máximo ${formatMoney(filters.maxTotal)}`, (current) => ({ ...current, maxTotal: '' })));
    }
    if ((filters.productId || '').trim()) {
        chips.push(chip('productId', 'Producto filtrado', (current) => ({ ...current, productId: '' })));
    }
    if ((filters.contificoCode || '').trim()) {
        chips.push(
            chip('contificoCode', `Código ${filters.contificoCode.trim()}`, (current) => ({
                ...current,
                contificoCode: '',
            }))
        );
    }
    if ((filters.keyword || '').trim()) {
        chips.push(chip('keyword', `Búsqueda: ${filters.keyword.trim()}`, (current) => ({ ...current, keyword: '' })));
    }
    if (filters.includeCancelled) {
        chips.push(chip('includeCancelled', 'Incluye canceladas', (current) => ({ ...current, includeCancelled: false })));
    }
    if (filters.includeQuotations) {
        chips.push(
            chip('includeQuotations', 'Incluye cotizaciones', (current) => ({ ...current, includeQuotations: false }))
        );
    }
    return chips;
};

export const hasAdvancedFilters = (filters) =>
    Boolean(
        filters.orderStatus?.length ||
            filters.paymentMethod?.length ||
            filters.isPaid ||
            filters.isDelivered ||
            filters.finished ||
            filters.customerType ||
            (filters.city || '').trim() ||
            (filters.state || '').trim() ||
            (filters.discountCode || '').trim() ||
            filters.minTotal !== '' ||
            filters.maxTotal !== '' ||
            (filters.productId || '').trim() ||
            (filters.contificoCode || '').trim() ||
            (filters.keyword || '').trim() ||
            filters.includeCancelled ||
            filters.includeQuotations
    );

export const toggleSort = (currentField, nextField, sortOrder) => ({
    field: nextField,
    sortOrder: currentField === nextField && sortOrder === 'desc' ? 'asc' : 'desc',
});
